import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Heading, Spinner, Text, VStack } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { useAppConfig, useAuth, WithAuthorization } from "../../../../context";
import { DataMappingProvider } from "../../../aiDataMapping/state/DataMappingContext";
import { useSession } from "../../../aiDataMapping/state/useSession";
import { useChat } from "../../../aiDataMapping/state/useChat";
import {
  createFoundationSession,
  deleteSession,
  getSession,
  getMessages,
  importRows,
  importTargetStructure,
  fetchDataFrameList,
  listFoundationSessions,
} from "../../../aiDataMapping/api/dataMappingClient";
import { SessionLayout } from "../../../aiDataMapping/SessionLayout";
import {
  SessionPicker,
  SessionPickerLoading,
} from "../../../aiDataMapping/components/SessionPicker";
import { FoundationDataV2 } from "../../../../api";
import { toCuratedRows } from "../../../../utils/foundationCuratedRows";

/**
 * The frame the extracted rows are pushed into, named so the agent can refer to it.
 *
 * Carries the workstream as well as the year: a year holds three, and a name keyed on the year alone would make
 * the second session to open reuse the first workstream's frame and talk about its figures.
 */
const frameNameFor = (dataYear) =>
  `foundation_${slugOf(dataYear.questionGroup)}_${dataYear.year}`;

const slugOf = (questionGroup) =>
  (questionGroup || "data").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

/**
 * Why a call to the chat service failed, in terms that point at the fix.
 *
 * An axios error with NO `response` never reached the server — the chat runs in its own service on its own
 * host, so this is a connection failure rather than anything about the year or the user's access. Saying
 * "try again" there sends someone retrying a button that cannot work; naming it is what gets the service
 * started locally, or reported in a deployed environment.
 */
const describeSessionError = (err, fallback) => {
  if (err?.response === undefined && err?.request !== undefined) {
    return "Couldn't reach the chat service. It runs separately from the rest of the app — if this is a local environment, check it is running; otherwise it may be down.";
  }
  if (err?.response?.status === 401) {
    return "Your sign-in has expired. Reload the page to continue.";
  }
  return err?.response?.data?.detail || fallback;
};

/**
 * "Chat with data" for one Foundation Data v2 workstream — a (reporting year, workstream) pair.
 *
 * Reuses the data-sorting session machinery wholesale — `SessionLayout`, the chat transport, the working
 * panel — and differs only in what the session is bound to and how its data arrives. Forking that
 * machinery would mean two copies of a live WebSocket chat, which would drift.
 *
 * The bootstrap mirrors `aiDataMapping/index.jsx`'s state machine deliberately, including writing the
 * session id back into the URL so the link is resumable.
 */
function FoundationChatBootstrap() {
  const { sessionId, setSession, setFile } = useSession();
  const { setMessages } = useChat();
  const { callApi } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const didBootstrap = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | needs-year | picker | ready
  const [sessions, setSessions] = useState([]);
  const [maxSessions, setMaxSessions] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  /*
    The year's DATABASE id, resolved once during bootstrap and held here because the picker's "Start a new
    session" button needs it. It cannot be read off the session list: that button is shown precisely when
    the list is empty or failed, so taking the id from `sessions[0]` yielded undefined in every case where
    it was clicked — and `String(undefined)` is the truthy string "undefined", which the backend would have
    accepted, binding a session to a year that does not exist.
  */
  const [yearId, setYearId] = useState(null);

  const year = searchParams.get("year");

  /**
   * Pushes a year's extracted rows in, unless the session already holds them.
   *
   * Takes the year's DATABASE id and resolves the calendar year itself, deliberately. Two identifiers
   * describe a year here — `foundation_data_year.id`, which the session is bound to, and the calendar year,
   * which the Java rows endpoint is keyed by — and this function previously took "the year" from its
   * callers, who passed a different one each: creating a session passed the calendar year and resuming one
   * passed the id. So resuming fetched `/years/7/rows` and got a 404, and it only ever broke on resume.
   *
   * Resolving here means there is one kind of value to pass and no way for two call sites to disagree.
   *
   * The session's own id wins over the URL's `?year=`, matching how the carbon page treats its target: a
   * bookmarked link can carry a year that is not the one this session was built for.
   *
   * Conditional on the frame list rather than on "we just created this session", because the reaper
   * archives an idle session after 14 days and purges its frames — a resumed session can therefore come
   * back with no data, and rendering an empty grid there would look like the extraction had vanished.
   */
  /**
   * Push the datapoints this organisation asked for, so the agent can normalise towards them.
   *
   * Pushed rather than fetched by the service: this page already holds it, and one pushed frame serves both
   * readers — the agent joins it, and the coverage endpoint reads the same frame rather than asking Java
   * again on every frame change.
   *
   * Per WORKSTREAM, from the year row's own `questionGroup`: the datapoints wanted for targets are not the
   * ones wanted for emissions, and a session sorting one must not be steered by the other's list.
   *
   * Unconditional, unlike the rows: re-pushing is idempotent by replacement server-side and a structure can
   * be REPLACED between two sessions, so skipping when the frame exists would leave a resumed session
   * steering by a list the organisation has since changed.
   *
   * Never fatal. An organisation with no structure answers 204 and pushes nothing, and a failure here costs
   * the steering, not the session — which is exactly the behaviour before this existed.
   */
  const ensureTargetStructureLoaded = useCallback(
    async (id, dataYear) => {
      if (!dataYear?.questionGroup) return;
      try {
        const structure = await callApi(
          FoundationDataV2.getTargetStructure,
          dataYear.questionGroup,
        );
        if (!structure?.datapoints?.length) return;
        await importTargetStructure(id, structure.datapoints);
      } catch (err) {
        console.warn("could not load the target structure for this chat", err);
      }
    },
    [callApi],
  );

  const ensureRowsLoaded = useCallback(
    async (id, yearIdValue) => {
      const years = await callApi(FoundationDataV2.listYears);
      const match = (years || []).find((y) => String(y.id) === String(yearIdValue));
      if (!match) {
        // The year was deleted under the session. Better to say so than to push another year's rows in.
        throw new Error("This chat's reporting year no longer exists.");
      }
      // Before the rows, so the agent's first look at the session already has the list to check against.
      // Awaited rather than fired off: the greeting reads coverage, and coverage needs this frame present.
      await ensureTargetStructureLoaded(id, match);
      const frameName = frameNameFor(match);
      try {
        const frames = await fetchDataFrameList(id);
        if ((frames || []).some((f) => f.key === frameName)) return;
      } catch {
        // Can't tell what the session holds; fall through and let the push decide. A 409 on a name
        // collision is harmless here — it means the frame was already there.
      }
      // `match.id`, not `match.year`: the rows endpoint is keyed on the workstream row — which is the same
      // id the session is bound to — and a year now holds one row per workstream.
      const rows = toCuratedRows(await callApi(FoundationDataV2.listRows, match.id));
      if (!rows.length) return;
      try {
        await importRows(id, frameName, rows);
      } catch (err) {
        if (err?.response?.status !== 409) throw err;
      }
    },
    [callApi, ensureTargetStructureLoaded],
  );

  const loadSession = useCallback(
    async (id) => {
      const info = await getSession(id);
      setSession(info.id, info.status, info);
      setFile(info.has_file, info.source_file_name);
      await ensureRowsLoaded(id, info.foundation_year_id);
      try {
        const { messages } = await getMessages(info.id);
        if (messages.length) setMessages(messages);
      } catch {
        // No history yet — fine.
      }
      const next = new URLSearchParams(searchParams);
      next.set("session", info.id);
      setSearchParams(next, { replace: true });
      setPhase("ready");
    },
    [setSession, setFile, setMessages, searchParams, setSearchParams, ensureRowsLoaded],
  );

  const startNewSession = useCallback(
    async (forYearId) => {
      setIsCreating(true);
      setCreateError(null);
      try {
        const s = await createFoundationSession(forYearId);
        setSession(s.id, s.status, s);
        await ensureRowsLoaded(s.id, forYearId);
        const next = new URLSearchParams(searchParams);
        next.set("session", s.id);
        setSearchParams(next, { replace: true });
        setPhase("ready");
      } catch (err) {
        setCreateError(
          describeSessionError(err, "Couldn't start a chat for this year. Try again, or reload the page."),
        );
      } finally {
        setIsCreating(false);
      }
    },
    [setSession, searchParams, setSearchParams, ensureRowsLoaded],
  );

  /**
   * `loadSession` for the picker's Continue button, with the rejection handled.
   *
   * The bootstrap effect wraps its own call in a try; this one had nothing, so any failure resuming a
   * session became an unhandled promise rejection — which CRA renders as a full-screen "Uncaught runtime
   * errors" overlay showing the raw axios message. That is worse than the error it is reporting: it hides
   * the page, says nothing about what to do, and looks like a crash rather than a failed request.
   */
  const resumeSession = useCallback(
    async (id) => {
      setCreateError(null);
      try {
        await loadSession(id);
      } catch (err) {
        setCreateError(describeSessionError(err, "Couldn't open that chat. Try again, or start a new one."));
      }
    },
    [loadSession],
  );

  const handleDelete = useCallback(async (id) => {
    // Caught here rather than left to SessionPicker, whose own handler is try/FINALLY — it clears its
    // spinner but does not stop the rejection, so a failed delete would also reach the window as an
    // uncaught error. The row is removed only on success, so a failure leaves the list truthful.
    try {
      await deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setCreateError(describeSessionError(err, "Couldn't delete that chat. Try again."));
    }
  }, []);

  useEffect(() => {
    if (didBootstrap.current || sessionId) return;
    didBootstrap.current = true;

    (async () => {
      const urlId = searchParams.get("session");
      if (urlId) {
        try {
          await loadSession(urlId);
          return;
        } catch {
          const next = new URLSearchParams(searchParams);
          next.delete("session");
          setSearchParams(next, { replace: true });
        }
      }

      // Read from the URL rather than the `year` const: this effect runs once on mount with an empty
      // dependency array, so a closed-over value would be the one from first render.
      const search = new URLSearchParams(window.location.search);
      const forDataYearId = search.get("dataYearId");
      const forYear = search.get("year");
      if (!forDataYearId && !forYear) {
        setPhase("needs-year");
        return;
      }

      let resolvedYearId = null;
      try {
        const years = await callApi(FoundationDataV2.listYears);
        /*
          The session binds to the workstream row's DATABASE id — two orgs both have a 2025, and a year now
          holds one row per workstream, so neither the calendar year nor the org alone identifies it.

          `?year=` is still accepted for links made before workstreams existed, and resolves to GHG Emissions:
          that is the workstream every figure extracted before the split belongs to, so an old link opens the
          session it was made for rather than an empty one.
        */
        const rows = years || [];
        resolvedYearId = forDataYearId
          ? rows.find((y) => String(y.id) === String(forDataYearId))?.id
          : rows.find(
              (y) => String(y.year) === String(forYear) && y.questionGroup === "GHG Emissions"
            )?.id;
        setYearId(resolvedYearId ?? null);
      } catch {
        setCreateError("Couldn't load this year. Try reloading the page.");
        setPhase("picker");
        return;
      }
      if (!resolvedYearId) {
        setPhase("needs-year");
        return;
      }

      try {
        const data = await listFoundationSessions(resolvedYearId);
        setMaxSessions(data.max_sessions ?? 0);
        if (!data.sessions?.length) {
          await startNewSession(resolvedYearId);
        } else {
          setSessions(data.sessions);
          setPhase("picker");
        }
      } catch (err) {
        // A failed list is not a reason to create: whatever stopped the list (auth, tenancy, the service
        // being down) would stop the create too, and the result would be an unexplained error instead of a
        // stated one.
        setCreateError(
          describeSessionError(err, "Couldn't load your chats for this year. Try reloading the page."),
        );
        setPhase("picker");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "loading") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="80vh">
        <Spinner size="md" mr={3} />
        <Text color="gray.600">Loading…</Text>
      </Box>
    );
  }

  if (phase === "needs-year") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="80vh" px={6}>
        <VStack spacing={3} maxW="480px" textAlign="center">
          <Heading size="md" color="gray.700">
            Open this from a Foundation Data workstream
          </Heading>
          <Text fontSize="body.s" color="gray.500">
            Chat with data works on one workstream&apos;s extracted metrics, so it needs to know which one.
            Open it with the <strong>Chat with data</strong> button on a Foundation Data page.
          </Text>
        </VStack>
      </Box>
    );
  }

  if (phase === "picker") {
    return (
      // SessionPicker renders only the list: it stopped carrying its own page frame and heading when AI
      // Mapping moved its picker into a modal, so each page-level caller supplies them. Same wrapper and
      // wording as aiDataMapping/index.jsx, which is the other one — a picker that reads as a page here
      // and a bare list there would be the same screen in two shapes.
      <Box maxW="640px" mx="auto" mt={10} px={4}>
        <Heading size="md" mb={2} color="darkTeal">
          Your sessions
        </Heading>
        <Text color="gray.600" mb={4}>
          Continue where you left off, or start a new session.
        </Text>
        <SessionPicker
          sessions={sessions}
          maxSessions={maxSessions}
          onContinue={(s) => resumeSession(s.id)}
          onDelete={handleDelete}
          onNew={() => startNewSession(yearId)}
          isCreating={isCreating}
          createError={createError}
          scopeLabel={year ? `Foundation Data ${year}` : "this Foundation Data year"}
        />
      </Box>
    );
  }

  if (!sessionId) {
    return <SessionPickerLoading />;
  }

  return <SessionLayout />;
}

function FoundationChatPage() {
  // Collapse the left nav so the chat workspace has maximum horizontal room, and restore the user's
  // previous state on unmount. Same reasoning as the AI Mapping page.
  const { storedSidebarState, setStoredSidebarState } = useAppConfig();
  const restoreSidebarRef = useRef(storedSidebarState);

  useEffect(() => {
    restoreSidebarRef.current = storedSidebarState;
    setStoredSidebarState(false);
    return () => setStoredSidebarState(restoreSidebarRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DataMappingProvider>
      <FoundationChatBootstrap />
    </DataMappingProvider>
  );
}

// Gated like every other page behind this HOC. `componentRequiredPermissions` and `module` arrive as props
// from the route element — the permissions under `linkProps` in paths.js only gate the sidebar link.
export default WithAuthorization(FoundationChatPage);
